import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { DotsIcon } from '../../components/icons';
import { RowMenu } from '../../components/PageMenu';
import { SwipeRow } from '../../components/SwipeRow';
import { outreachNextAction, outreachRank } from '../../lib/outreachMobile';
import { useIsMobile } from '../../lib/useIsMobile';
import { statusLabel, statusPillClass, quickAction } from './helpers';
import type { RankedLead, OutreachActions } from './types';

export function LeadRow({
  entry,
  actions,
  quick,
}: {
  entry: RankedLead;
  actions: OutreachActions;
  /** Queued rows browse only: acting on one means opening its dossier. */
  quick: boolean;
}) {
  const { lead, rank } = entry;
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const action = quickAction(lead);
  if (isMobile) {
    // The quick-action chip of the desktop row becomes a swipe-right on the
    // shortlist (§12 frame A) — and, so nothing is gesture-only, the same rung
    // is the first item of the row's "···" menu.
    const next = outreachNextAction(lead.status);
    const promoted = next.kind === 'promote' && lead.promoted_lead_id !== null;
    const label = promoted ? 'in Leads ↗' : next.label;
    const advance = () => {
      if (promoted) navigate(`/leads/${lead.promoted_lead_id}`);
      else if (next.kind === 'promote') actions.onPromote(lead);
      else actions.onStatus(lead, next.status);
    };
    const row = (
      <div className="outreach-mobile-row">
        <button
          type="button"
          className="outreach-mobile-open"
          onClick={() => actions.onOpenLead(lead.id)}
        >
          <span className="outreach-mobile-rank">{outreachRank(rank)}</span>
          <span className="outreach-mobile-main">
            <span className="outreach-mobile-name">{lead.name}</span>
            <span className="outreach-mobile-meta">
              {lead.suburb} ·{' '}
              <span className={`pill${lead.band.includes('retainer') ? ' gold' : ''}`}>
                {lead.band || 'unbanded'}
              </span>
            </span>
          </span>
          <span className="outreach-mobile-right">
            <span className={`outreach-mobile-score${quick ? '' : ' dim'}`}>{lead.score}</span>
            <span className={statusPillClass(lead)}>{statusLabel(lead)}</span>
          </span>
        </button>
        <button
          type="button"
          className="outreach-row-more"
          aria-label={`Actions for ${lead.name}`}
          onClick={() => setMenuOpen(true)}
        >
          <DotsIcon />
        </button>
      </div>
    );
    return (
      <>
        {quick ? (
          <SwipeRow
            rightAction={{ label, icon: '→', tone: 'gold', onSelect: advance }}
          >
            {row}
          </SwipeRow>
        ) : (
          row
        )}
        <RowMenu
          open={menuOpen}
          onClose={() => setMenuOpen(false)}
          title={lead.name}
          items={[
            ...(quick ? [{ key: 'next', label, onSelect: advance }] : []),
            { key: 'open', label: 'Open dossier', onSelect: () => actions.onOpenLead(lead.id) },
          ]}
        />
      </>
    );
  }
  const stop = (e: { stopPropagation: () => void }) => e.stopPropagation();
  return (
    <div className="row outreach-row" onClick={() => actions.onOpenLead(lead.id)}>
      <span className="outreach-rank">{String(rank).padStart(2, '0')}</span>
      <span className="row-title" title={lead.name}>
        {lead.name}
      </span>
      <span className="row-context">· {lead.suburb}</span>
      <span className="row-spacer" />
      <span className="outreach-score">{lead.score}</span>
      <span className="outreach-cell outreach-col-band">
        <span className={`pill${lead.band.includes('retainer') ? ' gold' : ''}`}>{lead.band || 'unbanded'}</span>
      </span>
      <span className="outreach-cell outreach-col-status">
        <span className={statusPillClass(lead)}>{statusLabel(lead)}</span>
      </span>
      <span className="outreach-col-act">
        {quick && action.kind === 'promoted' && (
          <Link className="chip" to={action.to} onClick={stop}>
            {action.label}
          </Link>
        )}
        {quick && action.kind === 'promote' && (
          <button
            type="button"
            className="chip accent"
            onClick={(e) => {
              stop(e);
              actions.onPromote(lead);
            }}
          >
            {action.label}
          </button>
        )}
        {quick && action.kind === 'status' && (
          <button
            type="button"
            className="chip"
            onClick={(e) => {
              stop(e);
              actions.onStatus(lead, action.status);
            }}
          >
            {action.label}
          </button>
        )}
      </span>
    </div>
  );
}
